import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Building2, Pencil, Users, ShieldCheck, KeyRound, Check, X, RotateCcw } from "lucide-react";
import { PaginationBar, usePagination } from "@/components/Pagination";

const menuLabel = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Establishment() {
  const [org, setOrg] = useState(null);
  const [branches, setBranches] = useState([]);
  const pg = usePagination(branches);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ address: "", mobile: "", email: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data: est } = await api.get("/establishment");
      setOrg(est.organisation || {});
      setBranches(est.branches || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (br) => {
    setEdit(br);
    setForm({ address: br.address || "", mobile: br.mobile || br.phone || "", email: br.email || "" });
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/establishment/branches/${edit.id}/contact`, form);
      toast.success("Establishment updated");
      setEdit(null); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-8" data-testid="establishment-page">
      <div className="mb-6">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Building2 className="w-3 h-3"/> Org &amp; Access · Establishment</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Establishment</h1>
        <p className="text-muted-foreground text-sm mt-1">Establishments of this organisation — contact details editable here; creation and GSTIN/PAN managed by the platform admin</p>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid="establishment-branches-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Establishment Name</th>
              <th className="text-left p-3">Address</th>
              <th className="text-left p-3">GSTIN</th>
              <th className="text-left p-3">Mobile</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map(br => (
              <tr key={br.id} data-testid={`branch-row-${br.id}`} className="border-t border-border">
                <td className="p-3 font-semibold">{br.name}</td>
                <td className="p-3 text-xs text-muted-foreground max-w-64 truncate">{br.address || "—"}</td>
                <td className="p-3 font-mono text-xs">{br.gstin || "—"}</td>
                <td className="p-3 font-mono text-xs">{br.mobile || br.phone || "—"}</td>
                <td className="p-3">{br.is_active
                  ? <Badge className="rounded-sm text-[10px] uppercase tracking-wider">Active</Badge>
                  : <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider text-muted-foreground">Inactive</Badge>}</td>
                <td className="p-3 text-right">
                  <Button data-testid={`branch-edit-${br.id}`} size="sm" variant="ghost" onClick={() => openEdit(br)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
            {!branches.length && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground text-xs uppercase tracking-widest" data-testid="est-empty">No establishments yet — ask your platform admin to create one</td></tr>}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => { if (!v) setEdit(null); }}>
        <DialogContent className="rounded-sm bg-card border-border max-w-md" data-testid="branch-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">{edit?.name}</DialogTitle>
            <DialogDescription className="text-xs">Update contact details. GSTIN, PAN and organisation are read-only.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">GSTIN (read-only)</Label>
                  <Input data-testid="branch-gstin-ro" value={edit.gstin || "—"} disabled className="mt-1 rounded-sm bg-secondary border-border font-mono opacity-60"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">PAN (read-only)</Label>
                  <Input data-testid="branch-pan-ro" value={edit.pan || "—"} disabled className="mt-1 rounded-sm bg-secondary border-border font-mono opacity-60"/>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Organisation (read-only)</Label>
                <Input data-testid="branch-org-ro" value={org?.business_name || "—"} disabled className="mt-1 rounded-sm bg-secondary border-border opacity-60"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label>
                <Textarea data-testid="branch-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Mobile</Label>
                  <Input data-testid="branch-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="mt-1 rounded-sm bg-secondary border-border font-mono"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
                  <Input data-testid="branch-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm bg-secondary border-border"/>
                </div>
              </div>
              <Button data-testid="branch-save" onClick={save} disabled={busy} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
                {busy ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function EstablishmentUsers() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const load = async () => {
    try {
      const [{ data: est }, { data: rl }, { data: us }] = await Promise.all([
        api.get("/establishment"), api.get("/establishment/roles"), api.get("/establishment/users"),
      ]);
      setBranches(est.branches || []);
      setRoles(rl.roles || []);
      setUsers(us || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="p-8" data-testid="establishment-users-page">
      <div className="mb-6">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Users className="w-3 h-3"/> Org & Access · Establishment</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">User</h1>
        <p className="text-muted-foreground text-sm mt-1">Users of this organisation and their branch assignments</p>
      </div>
      <UsersTab users={users} branches={branches} roles={roles} onChanged={load}/>
    </div>
  );
}

export function EstablishmentRoles() {
  const [rolesData, setRolesData] = useState({ menu_keys: [], actions: [], roles: [] });
  const load = async () => {
    try {
      const { data: rl } = await api.get("/establishment/roles");
      setRolesData(rl);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="p-8" data-testid="establishment-roles-page">
      <div className="mb-6">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><ShieldCheck className="w-3 h-3"/> Org & Access · Establishment</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Role &amp; Permission Master</h1>
        <p className="text-muted-foreground text-sm mt-1">View roles and edit permission settings for this organisation</p>
      </div>
      <RolesTab rolesData={rolesData} onChanged={load}/>
    </div>
  );
}

export function EstablishmentPermissions() {
  const [users, setUsers] = useState([]);
  const [rolesData, setRolesData] = useState({ menu_keys: [], actions: [], roles: [] });
  const load = async () => {
    try {
      const [{ data: rl }, { data: us }] = await Promise.all([
        api.get("/establishment/roles"), api.get("/establishment/users"),
      ]);
      setRolesData(rl);
      setUsers(us || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="p-8" data-testid="establishment-permissions-page">
      <div className="mb-6">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><KeyRound className="w-3 h-3"/> Org & Access · Establishment</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Permission</h1>
        <p className="text-muted-foreground text-sm mt-1">Per-user permission overrides for this organisation</p>
      </div>
      <PermissionsTab users={users} rolesData={rolesData}/>
    </div>
  );
}

function UsersTab({ users, branches, roles, onChanged }) {
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const pg2 = usePagination(users);
  const branchName = (id) => branches.find((x) => x.id === id)?.name || "—";
  const roleNames = roles.map((r) => r.name);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/establishment/users/${edit.id}`, {
        name: edit.name, phone: edit.phone, role: edit.role,
        branch_id: edit.branch_id || null, is_active: edit.is_active !== false,
      });
      toast.success("User updated");
      setEdit(null); onChanged();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid="est-users-card">
      <div className="p-4 border-b border-border">
        <div className="text-primary text-[10px] uppercase tracking-[0.25em] flex items-center gap-1"><Users className="w-3 h-3"/> Organisation Users</div>
        <h2 className="font-display font-black text-xl uppercase tracking-tighter">Users</h2>
      </div>
      <table className="w-full text-sm" data-testid="est-users-table">
        <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          <tr>
            <th className="text-left p-3">Name</th><th className="text-left p-3">Email</th>
            <th className="text-left p-3">Phone</th><th className="text-left p-3">Role</th>
            <th className="text-left p-3">Branch</th><th className="text-left p-3">Status</th>
            <th className="text-right p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pg2.pageItems.map(u => (
            <tr key={u.id} data-testid={`est-user-row-${u.id}`} className="border-t border-border">
              <td className="p-3 font-semibold">{u.name}</td>
              <td className="p-3 font-mono text-xs">{u.email}</td>
              <td className="p-3 font-mono text-xs">{u.phone || "—"}</td>
              <td className="p-3"><Badge variant="outline" className="rounded-sm uppercase text-[10px] tracking-widest">{u.role}</Badge></td>
              <td className="p-3 text-xs">{branchName(u.branch_id)}</td>
              <td className="p-3 text-xs">{u.is_active !== false ? <span className="text-primary">Active</span> : <span className="text-muted-foreground">Inactive</span>}</td>
              <td className="p-3 text-right">
                <Button data-testid={`est-user-edit-${u.id}`} size="sm" variant="ghost" onClick={() => setEdit({ ...u })} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
              </td>
            </tr>
          ))}
          {!users.length && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground text-xs uppercase tracking-widest">No users in this organisation</td></tr>}
        </tbody>
      </table>
      <PaginationBar pg={pg2} testid="pagination-2"/>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="rounded-sm bg-card border-border max-w-md" data-testid="est-user-dialog">
          <DialogHeader><DialogTitle className="font-display uppercase">Edit User</DialogTitle>
            <DialogDescription className="sr-only">Edit this user's name, phone, role, branch and active status</DialogDescription></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Name</Label>
                <Input data-testid="est-user-name" value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="mt-1 rounded-sm bg-secondary border-border"/></div>
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
                <Input data-testid="est-user-email" value={edit.email || ""} disabled className="mt-1 rounded-sm bg-secondary border-border font-mono opacity-60"/></div>
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone</Label>
                <Input data-testid="est-user-phone" value={edit.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} className="mt-1 rounded-sm bg-secondary border-border font-mono"/></div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Role</Label>
                <Select value={edit.role} onValueChange={(v) => setEdit({ ...edit, role: v })}>
                  <SelectTrigger data-testid="est-user-role" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select role"/></SelectTrigger>
                  <SelectContent className="rounded-sm bg-card border-border">
                    {roleNames.map(r => <SelectItem key={r} data-testid={`est-user-role-${r}`} value={r} className="uppercase text-xs tracking-widest">{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Branch</Label>
                <Select value={edit.branch_id || "__none__"} onValueChange={(v) => setEdit({ ...edit, branch_id: v === "__none__" ? null : v })}>
                  <SelectTrigger data-testid="est-user-branch" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select branch"/></SelectTrigger>
                  <SelectContent className="rounded-sm bg-card border-border">
                    <SelectItem data-testid="est-user-branch-none" value="__none__">— None —</SelectItem>
                    {branches.map(br => <SelectItem key={br.id} data-testid={`est-user-branch-${br.id}`} value={br.id}>{br.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Active</Label>
                <Switch data-testid="est-user-active" checked={edit.is_active !== false} onCheckedChange={(v) => setEdit({ ...edit, is_active: v })}/>
              </div>
              <Button data-testid="est-user-save" onClick={save} disabled={busy} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Save User</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RolesTab({ rolesData, onChanged }) {
  const [sel, setSel] = useState(null);
  const [editRole, setEditRole] = useState(null);
  const [editMenus, setEditMenus] = useState({});
  const [busy, setBusy] = useState(false);
  const { menu_keys, actions, roles } = rolesData;
  const current = roles.find((r) => r.name === sel) || roles[0];

  useEffect(() => {
    if (!sel && roles.length) setSel(roles[0].name);
  }, [roles, sel]);

  const openEdit = (r) => {
    setEditRole(r);
    setEditMenus(JSON.parse(JSON.stringify(r.menus || {})));
  };
  const toggleCell = (m, a) =>
    setEditMenus(prev => ({ ...prev, [m]: { ...prev[m], [a]: !prev[m]?.[a] } }));
  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/establishment/roles/${editRole.name}/permissions`, { menus: editMenus });
      toast.success(`Permissions updated for ${editRole.name}`);
      setEditRole(null);
      onChanged?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-3 gap-6" data-testid="est-roles-card">
      <div className="border border-border bg-card rounded-sm overflow-hidden self-start">
        <div className="p-4 border-b border-border">
          <div className="text-primary text-[10px] uppercase tracking-[0.25em] flex items-center gap-1"><ShieldCheck className="w-3 h-3"/> Role Master</div>
          <h2 className="font-display font-black text-xl uppercase tracking-tighter">Roles</h2>
        </div>
        <div data-testid="est-roles-list">
          {roles.map(r => (
            <div key={r.name} data-testid={`est-role-${r.name}`} onClick={() => setSel(r.name)}
              className={`w-full text-left px-4 py-3 border-t border-border flex items-center justify-between transition-colors cursor-pointer ${current?.name === r.name ? "bg-secondary" : "hover:bg-secondary/50"}`}>
              <div>
                <div className="text-sm font-semibold uppercase tracking-widest">{r.name}</div>
                <div className="text-xs text-muted-foreground">{r.description || "Custom role"}</div>
              </div>
              <div className="flex items-center gap-2">
                {r.is_system && <Badge variant="outline" className="rounded-sm text-[9px] uppercase tracking-widest">System</Badge>}
                <Button size="sm" variant="ghost" data-testid={`est-role-edit-${r.name}`}
                  onClick={(e) => { e.stopPropagation(); openEdit(r); }} className="h-7 w-7 p-0" title="Edit permissions">
                  <Pencil className="w-3 h-3"/>
                </Button>
              </div>
            </div>
          ))}
          {!roles.length && <div className="p-8 text-center text-muted-foreground text-xs uppercase tracking-widest">Loading roles…</div>}
        </div>
      </div>
      <div className="col-span-2 border border-border bg-card rounded-sm overflow-hidden self-start">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-black text-xl uppercase tracking-tighter" data-testid="est-role-matrix-title">{current ? `${current.name} Permissions` : "Permissions"}</h2>
          <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Click Edit to modify</span>
        </div>
        <table className="w-full text-sm" data-testid="est-role-matrix">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Menu</th>
              {actions.map(a => <th key={a} className="p-3 text-center w-20">{a}</th>)}
            </tr>
          </thead>
          <tbody>
            {menu_keys.map(m => (
              <tr key={m} data-testid={`est-role-row-${m}`} className="border-t border-border">
                <td className="p-3 text-xs font-semibold uppercase tracking-widest">{menuLabel(m)}</td>
                {actions.map(a => (
                  <td key={a} className="p-3 text-center" data-testid={`est-role-cell-${m}-${a}`}>
                    {current?.menus?.[m]?.[a]
                      ? <Check className="w-4 h-4 text-primary inline"/>
                      : <X className="w-4 h-4 text-muted-foreground/40 inline"/>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editRole} onOpenChange={(v) => { if (!v) setEditRole(null); }}>
        <DialogContent className="rounded-sm bg-card border-border max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="est-role-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">Edit Permissions · {editRole?.name}</DialogTitle>
            <DialogDescription className="text-xs">Role name and description are read-only. Adjust the permission checkboxes and save.</DialogDescription>
          </DialogHeader>
          {editRole && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Role Name (read-only)</Label>
                  <Input data-testid="est-role-edit-name" value={editRole.name} disabled className="mt-1 rounded-sm bg-secondary border-border opacity-60"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Description (read-only)</Label>
                  <Input data-testid="est-role-edit-desc" value={editRole.description || ""} disabled className="mt-1 rounded-sm bg-secondary border-border opacity-60"/>
                </div>
              </div>
              <table className="w-full text-sm border border-border" data-testid="est-role-edit-matrix">
                <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  <tr>
                    <th className="text-left p-2 pl-3">Menu</th>
                    {actions.map(a => <th key={a} className="p-2 text-center w-20">{a}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {menu_keys.map(m => (
                    <tr key={m} data-testid={`est-role-edit-row-${m}`} className="border-t border-border">
                      <td className="p-2 pl-3 text-xs font-semibold uppercase tracking-widest">{menuLabel(m)}</td>
                      {actions.map(a => (
                        <td key={a} className="p-2 text-center">
                          <Checkbox data-testid={`est-role-edit-${m}-${a}`}
                            checked={!!editMenus[m]?.[a]} onCheckedChange={() => toggleCell(m, a)}/>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <Button data-testid="est-role-edit-save" onClick={saveEdit} disabled={busy}
                className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
                {busy ? "Saving…" : "Save Permissions"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PermissionsTab({ users, rolesData }) {
  const { menu_keys, actions } = rolesData;
  const [sel, setSel] = useState("");
  const [data, setData] = useState(null);
  const [grid, setGrid] = useState({});
  const [busy, setBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  const staffUsers = useMemo(() => users.filter(u => u.role !== "customer"), [users]);

  const loadUser = async (uid) => {
    if (!uid) { setData(null); return; }
    try {
      const { data } = await api.get(`/establishment/user-permissions/${uid}`);
      setData(data);
      setGrid(JSON.parse(JSON.stringify(data.effective || {})));
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { loadUser(sel); }, [sel]);

  const toggle = (m, a) =>
    setGrid(g => ({ ...g, [m]: { ...g[m], [a]: !(g[m] && g[m][a]) } }));

  const dirty = data && JSON.stringify(grid) !== JSON.stringify(data.effective);
  const hasOverrides = data && Object.keys(data.overrides || {}).length > 0;

  const save = async () => {
    setBusy(true);
    try {
      const diff = {};
      for (const m of menu_keys) {
        const row = grid[m] || {};
        const base = (data.role_menus || {})[m] || {};
        if (actions.some(a => !!row[a] !== !!base[a]))
          diff[m] = Object.fromEntries(actions.map(a => [a, !!row[a]]));
      }
      await api.put(`/establishment/user-permissions/${sel}`, { menus: diff });
      toast.success("Permission overrides saved");
      loadUser(sel);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    try {
      await api.delete(`/establishment/user-permissions/${sel}`);
      toast.success("Reset to role defaults");
      setResetOpen(false);
      loadUser(sel);
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid="est-permissions-card">
      <div className="p-4 border-b border-border flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-primary text-[10px] uppercase tracking-[0.25em] flex items-center gap-1"><KeyRound className="w-3 h-3"/> User-Level Overrides</div>
          <h2 className="font-display font-black text-xl uppercase tracking-tighter">Permission</h2>
        </div>
        <div className="w-72">
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Select User</Label>
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger data-testid="est-perm-user-select" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Choose a user…"/></SelectTrigger>
            <SelectContent className="rounded-sm bg-card border-border">
              {staffUsers.map(u => (
                <SelectItem key={u.id} data-testid={`est-perm-user-${u.id}`} value={u.id}>{u.name} · {u.role}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!data && <div className="p-10 text-center text-muted-foreground text-xs uppercase tracking-widest" data-testid="est-perm-empty">Select a user to manage permission overrides</div>}

      {data && (
        <>
          <div className="px-4 py-3 border-b border-border flex items-center gap-3 text-xs">
            <span className="text-muted-foreground uppercase tracking-widest">{data.user.name} · role</span>
            <Badge variant="outline" className="rounded-sm uppercase text-[10px] tracking-widest">{data.user.role}</Badge>
            {hasOverrides
              ? <Badge data-testid="est-perm-customised-badge" className="rounded-sm uppercase text-[10px] tracking-widest bg-primary text-primary-foreground">Customised</Badge>
              : <Badge data-testid="est-perm-default-badge" variant="outline" className="rounded-sm uppercase text-[10px] tracking-widest">Role Defaults</Badge>}
          </div>
          <table className="w-full text-sm" data-testid="est-perm-matrix">
            <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <tr>
                <th className="text-left p-3">Menu</th>
                {actions.map(a => <th key={a} className="p-3 text-center w-20">{a}</th>)}
              </tr>
            </thead>
            <tbody>
              {menu_keys.map(m => (
                <tr key={m} data-testid={`est-perm-row-${m}`} className="border-t border-border">
                  <td className="p-3 text-xs font-semibold uppercase tracking-widest">
                    {menuLabel(m)}
                    {(data.overrides || {})[m] && <span className="ml-2 text-[9px] text-primary uppercase tracking-widest">override</span>}
                  </td>
                  {actions.map(a => (
                    <td key={a} className="p-3 text-center">
                      <Checkbox data-testid={`est-perm-${m}-${a}`} checked={!!(grid[m] && grid[m][a])} onCheckedChange={() => toggle(m, a)}/>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex gap-2 p-4 border-t border-border">
            <Button data-testid="est-perm-save" onClick={save} disabled={busy || !dirty} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">Save Overrides</Button>
            <Button data-testid="est-perm-reset" variant="outline" onClick={() => setResetOpen(true)} disabled={!hasOverrides} className="rounded-sm uppercase tracking-[0.15em] text-xs">
              <RotateCcw className="w-3.5 h-3.5 mr-1"/> Reset to Role Defaults
            </Button>
          </div>
          <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
            <AlertDialogContent className="rounded-sm bg-card border-border" data-testid="est-perm-reset-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display uppercase">Reset Permissions?</AlertDialogTitle>
                <AlertDialogDescription>This removes all custom overrides for {data.user.name} and restores their role defaults.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="est-perm-reset-cancel" className="rounded-sm uppercase tracking-[0.15em] text-xs">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="est-perm-reset-confirm" onClick={reset} className="rounded-sm uppercase tracking-[0.15em] text-xs">Reset</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
